import {useFlags} from "launchdarkly-react-client-sdk";
import {useState, useEffect, useMemo} from "react";

const camelToHyphenCache = new Map();
const camelToHyphen = (str) => {
  if (camelToHyphenCache.has(str)) {
    return camelToHyphenCache.get(str);
  }
  const result = str.replace(/([A-Z])/g, "-$1").toLowerCase();
  camelToHyphenCache.set(str, result);
  return result;
};

const hyphenToCamelCache = new Map();
const hyphenToCamel = (str) => {
  if (hyphenToCamelCache.has(str)) {
    return hyphenToCamelCache.get(str);
  }
  const result = str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
  hyphenToCamelCache.set(str, result);
  return result;
};

const createDualFlags = (flags) => {
  const dualFlags = {...flags};

  Object.keys(flags).forEach((key) => {
    const value = flags[key];
    if (key.match(/[A-Z]/)) {
      dualFlags[camelToHyphen(key)] = value;
    } else if (key.includes("-")) {
      dualFlags[hyphenToCamel(key)] = value;
    }
  });

  return dualFlags;
};

/**
 * Feature Flag Override System
 *
 * This module provides a way to override LaunchDarkly feature flags during development.
 *
 * To use overrides:
 * 1. Create a file named 'flagoverrides.json' in the public/ directory
 * 2. Add your overrides in JSON format using either style:
 *    {
 *      "newInbox": true,        // camelCase style
 *      "inbox-panels": true,    // hyphen style
 *      "cannedResponses": false  // both styles will work
 *    }
 *
 * The flags can then be accessed using either style:
 * - flags.newInbox or flags["new-inbox"]
 * - flags.inboxPanels or flags["inbox-panels"]

 * Usage example in components:
 * ```
 * import {useFeatureFlags} from "utilities/featureUtil";
 *
 * function MyComponent() {
 *   const flags = useFeatureFlags();
 *
 *   // Both styles work:
 *   if (flags.newInbox || flags["new-inbox"]) {
 *     return <NewInbox />;
 *   }
 *   return <OldInbox />;
 * }
 * ```
 *
 * Note: Flag overrides only work in development mode. In production,
 * the actual LaunchDarkly values will be used.
 */
export const useFeatureFlags = () => {
  const flags = useFlags();
  const [localOverrides, setLocalOverrides] = useState({});

  useEffect(() => {
    let mounted = true;

    if (process.env.NODE_ENV === "development") {
      fetch("/flagoverrides.json")
        .then((response) => {
          if (!response.ok) {
            return {};
          }
          return response.json();
        })
        .then((data) => {
          if (mounted) {
            const overrides = createDualFlags(data);
            setLocalOverrides(overrides);
          }
        })
        .catch(() => {
          if (mounted) {
            setLocalOverrides({});
          }
        });
    }

    return () => {
      mounted = false;
    };
  }, []);

  return useMemo(() => {
    const ldFlags = createDualFlags(flags);
    return {
      ...ldFlags,
      ...localOverrides,
    };
  }, [flags, localOverrides]);
};
